import { useState, useEffect } from 'react';
import {
  ArrowLeft, BookOpen, TrendingUp, AlertTriangle, Award, FileText,
  BarChart3, Printer, CheckCircle, Clock, XCircle, Users, CalendarDays,
  ShieldCheck, HelpCircle, Download, CheckCircle2
} from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { api } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, Legend, ReferenceLine
} from 'recharts';
import { UserAvatar } from '../../components/ui/UserAvatar';
import './responsavel-mobile.css';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from './useResponsavelId';
import { ResponsavelTabBar } from '../../components/responsavel/ResponsavelTabBar';

interface DisciplinaBoletim {
  materia: string;
  bim1?: number | null;
  bim2?: number | null;
  bim3?: number | null;
  bim4?: number | null;
  media?: number | null;
  faltas: number;
  situacao: 'APROVADO' | 'RECUPERACAO' | 'REPROVADO' | 'EM_CURSO';
}

interface FilhoResumo {
  id: string;
  nome: string;
  matricula?: string;
  fotoUrl?: string;
  turma?: { nome: string; serie?: string; turno?: string };
  escola?: { nome: string };
  frequenciaGlobal: number;
  faltasTotais: number;
  diasLetivos: number;
  mediaGlobal?: number | null;
  totalAvaliacoes: number;
  evolucao: { name: string; nota: number }[];
  disciplinasBoletim: DisciplinaBoletim[];
}

function useIsMobile(bp = 768) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < bp);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [bp]);
  return isMobile;
}

export function Desempenho() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const responsavelId = useResponsavelId();

  const [filhos, setFilhos] = useState<FilhoResumo[]>([]);
  const [selectedFilhoId, setSelectedFilhoId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'boletim' | 'graficos'>('boletim');

  useEffect(() => {
    if (!responsavelId) return;
    
    setLoading(true);
    api.get(ROUTES.mobileResponsavel.desempenho(responsavelId))
      .then((res) => {
        const rawList = Array.isArray(res.data) ? res.data : [];
        const dataFilhos: FilhoResumo[] = rawList.map((item: any) => {
          const rawBoletim = Array.isArray(item.boletim) ? item.boletim : [];
          const rawEvolucao = Array.isArray(item.evolucao) ? item.evolucao : [];

          const disciplinasBoletim: DisciplinaBoletim[] = rawBoletim.map((b: any) => ({
            materia: b.materia || 'Componente Curricular',
            bim1: typeof b.bim1 === 'number' ? b.bim1 : null,
            bim2: typeof b.bim2 === 'number' ? b.bim2 : null,
            bim3: typeof b.bim3 === 'number' ? b.bim3 : null,
            bim4: typeof b.bim4 === 'number' ? b.bim4 : null,
            media: typeof b.media === 'number' ? b.media : null,
            faltas: typeof b.faltas === 'number' ? b.faltas : 0,
            situacao: (b.situacao || 'EM_CURSO') as DisciplinaBoletim['situacao'],
          }));

          const evolucaoFormatada = rawEvolucao.map((e: any) => ({
            name: e.bimestre || 'Bimestre',
            nota: typeof e.media === 'number' ? e.media : 0,
          }));

          return {
            id: item.aluno?.id || `aluno-${Math.random()}`,
            nome: item.aluno?.nome || 'Dependente',
            matricula: item.aluno?.matricula || undefined,
            fotoUrl: item.aluno?.fotoUrl || undefined,
            turma: item.aluno?.turma || undefined,
            escola: item.aluno?.escola || { nome: user?.nomeEscola || 'Escola Vinculada' },
            frequenciaGlobal: item.frequencia?.porcentagem ?? 100,
            faltasTotais: item.frequencia?.ausencias ?? 0,
            diasLetivos: item.frequencia?.diasLetivos ?? 0,
            mediaGlobal: typeof item.mediaGlobal === 'number' ? item.mediaGlobal : null,
            totalAvaliacoes: item.totalAvaliacoes ?? 0,
            evolucao: evolucaoFormatada,
            disciplinasBoletim,
          };
        });

        setFilhos(dataFilhos);
        if (dataFilhos.length > 0) setSelectedFilhoId(dataFilhos[0].id);
      })
      .catch((err) => {
        console.error('Erro ao buscar desempenho real:', err);
        setFilhos([]);
      })
      .finally(() => setLoading(false));
  }, [responsavelId, user?.nomeEscola]);

  const selectedFilho = filhos.find(f => f.id === selectedFilhoId);

  // ═══ DESKTOP VIEW ═══
  if (!isMobile) {
    return (
      <div className="admin-page" style={{ maxWidth: 1400, width: '100%', margin: '0 auto', paddingBottom: 32 }}>
        {/* ═══ Standardized Full-Width Hero Banner ═══ */}
        <div style={{
          background: 'radial-gradient(135% 140% at 100% 0%, #312e81 0%, #1e1b4b 55%, #0f172a 100%)',
          borderRadius: 20,
          padding: '20px 28px',
          color: '#ffffff',
          boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ minWidth: 260, position: 'relative', zIndex: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
              padding: '3px 10px', borderRadius: 20, fontSize: '0.7rem', fontWeight: 600,
              color: '#e0e7ff', marginBottom: 6,
            }}>
              <Award size={12} color="#818cf8" /> Rendimento & Boletim Escolar Oficial
            </div>
            <h1 style={{ fontSize: '1.45rem', fontWeight: 800, margin: 0, color: '#ffffff', letterSpacing: '-0.02em' }}>
              Boletim & Desempenho Acadêmico
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.8rem', color: 'rgba(255,255,255,0.72)' }}>
              Matriz oficial de notas por disciplina, médias bimestrais e frequência conforme diretrizes do MEC / LDB
            </p>
          </div>

          {/* Hero KPIs Reais */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', position: 'relative', zIndex: 2 }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#38bdf8', lineHeight: 1.1 }}>
                {selectedFilho?.mediaGlobal !== null && selectedFilho?.mediaGlobal !== undefined
                  ? selectedFilho.mediaGlobal.toFixed(1)
                  : '—'}
              </div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Média Geral</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: (selectedFilho?.frequenciaGlobal ?? 0) >= 75 ? '#4ade80' : '#fb923c', lineHeight: 1.1 }}>
                {selectedFilho?.frequenciaGlobal ?? 0}%
              </div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Frequência</div>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14,
              padding: '8px 16px', textAlign: 'center', minWidth: 85,
            }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f472b6', lineHeight: 1.1 }}>
                {selectedFilho?.disciplinasBoletim?.length ?? 0}
              </div>
              <div style={{ fontSize: '0.62rem', fontWeight: 700, color: 'rgba(255,255,255,0.65)', textTransform: 'uppercase', marginTop: 3 }}>Disciplinas</div>
            </div>
          </div>
        </div>

        {/* Toolbar: Student Selector & Tabs */}
        <div style={{
          background: '#ffffff',
          borderRadius: 16,
          padding: '12px 18px',
          marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12
        }}>
          {/* Seletor de Filho */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#64748b' }}>Aluno:</span>
            {filhos.map((f) => {
              const isSelected = selectedFilhoId === f.id;
              return (
                <button
                  key={f.id}
                  onClick={() => setSelectedFilhoId(f.id)}
                  style={{
                    padding: '6px 14px', borderRadius: 10, border: '1px solid',
                    borderColor: isSelected ? '#2563eb' : '#e2e8f0',
                    background: isSelected ? '#eff6ff' : '#ffffff',
                    color: isSelected ? '#2563eb' : '#334155',
                    fontWeight: isSelected ? 800 : 500, fontSize: '0.8rem',
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)', transition: 'all 0.15s',
                  }}
                >
                  <UserAvatar src={f.fotoUrl} name={f.nome} role="ALUNO" size="xs" />
                  <span>{f.nome}</span>
                  {f.turma?.nome && <span style={{ fontSize: '0.7rem', opacity: 0.75 }}>({f.turma.nome})</span>}
                </button>
              );
            })}
          </div>

          {/* Abas: Boletim Oficial vs Gráficos */}
          <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 4, gap: 4 }}>
            <button
              onClick={() => setActiveTab('boletim')}
              style={{
                padding: '7px 16px', borderRadius: 8, border: 'none',
                background: activeTab === 'boletim' ? '#ffffff' : 'transparent',
                color: activeTab === 'boletim' ? '#0f172a' : '#64748b',
                fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: activeTab === 'boletim' ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              <FileText size={15} color={activeTab === 'boletim' ? '#2563eb' : '#64748b'} />
              Boletim Oficial
            </button>
            <button
              onClick={() => setActiveTab('graficos')}
              style={{
                padding: '7px 16px', borderRadius: 8, border: 'none',
                background: activeTab === 'graficos' ? '#ffffff' : 'transparent',
                color: activeTab === 'graficos' ? '#0f172a' : '#64748b',
                fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
                boxShadow: activeTab === 'graficos' ? '0 1px 4px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              <BarChart3 size={15} color={activeTab === 'graficos' ? '#2563eb' : '#64748b'} />
              Gráficos & Evolução
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>Carregando boletim escolar...</div>
        ) : !selectedFilho ? (
          <div style={{ textAlign: 'center', padding: 48, background: '#fff', borderRadius: 20, border: '1px solid #e2e8f0' }}>
            <BookOpen size={40} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '1rem', fontWeight: 700 }}>Nenhum dependente localizado</h3>
          </div>
        ) : activeTab === 'boletim' ? (
          /* ═══ ABA 1: BOLETIM ESCOLAR OFICIAL (TABELA + PAINEL LATERAL) ═══ */
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 0.65fr)',
            gap: 20,
            alignItems: 'start',
            width: '100%',
          }}>
            {/* Tabela Oficial do Boletim */}
            <div style={{
              background: '#ffffff',
              borderRadius: 20,
              border: '1px solid #e2e8f0',
              padding: 24,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: '#0f172a' }}>
                    Boletim de Rendimento Escolar — Ano Letivo 2026
                  </h3>
                  <p style={{ margin: '3px 0 0', fontSize: '0.76rem', color: '#64748b' }}>
                    {selectedFilho.escola?.nome || 'Escola Municipal'} • {selectedFilho.turma?.nome || 'Turma em definição'} • Média para aprovação: 6.0
                  </p>
                </div>

                <button
                  onClick={() => window.print()}
                  style={{
                    background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155',
                    borderRadius: 10, padding: '7px 14px', fontSize: '0.78rem', fontWeight: 700,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  }}
                >
                  <Printer size={15} /> Imprimir Boletim (PDF)
                </button>
              </div>

              {selectedFilho.disciplinasBoletim.length === 0 ? (
                <div style={{
                  padding: '36px 20px', textAlign: 'center', background: '#f8fafc',
                  borderRadius: 14, border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10
                }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb' }}>
                    <FileText size={24} />
                  </div>
                  <h4 style={{ margin: 0, fontSize: '0.96rem', fontWeight: 800, color: '#0f172a' }}>
                    Ciclo Avaliativo em Andamento
                  </h4>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b', maxWidth: 480, lineHeight: 1.5 }}>
                    As notas e médias bimestrais serão publicadas pelos professores regentes conforme o fechamento dos ciclos avaliativos do ano letivo. A assiduidade está registrada com {selectedFilho.frequenciaGlobal}% de presença.
                  </p>
                </div>
              ) : (
                /* Tabela de Notas Real */
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <th style={{ padding: '12px 14px', fontWeight: 800 }}>Componente Curricular / Disciplina</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700 }}>1º Bim</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700 }}>2º Bim</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700 }}>3º Bim</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700 }}>4º Bim</th>
                        <th style={{ padding: '12px 12px', textAlign: 'center', fontWeight: 800, color: '#0f172a', background: '#f1f5f9' }}>Média</th>
                        <th style={{ padding: '12px 10px', textAlign: 'center', fontWeight: 700 }}>Faltas</th>
                        <th style={{ padding: '12px 14px', textAlign: 'center', fontWeight: 800 }}>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedFilho.disciplinasBoletim.map((disc, idx) => {
                        const isEven = idx % 2 === 0;
                        return (
                          <tr key={disc.materia} style={{ background: isEven ? '#ffffff' : '#fbfcfd', borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '12px 14px', fontWeight: 700, color: '#1e293b' }}>
                              {disc.materia}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'center', color: (disc.bim1 ?? 0) >= 6 ? '#16a34a' : (disc.bim1 !== null ? '#dc2626' : '#94a3b8'), fontWeight: disc.bim1 !== null ? 700 : 400 }}>
                              {disc.bim1 !== null && disc.bim1 !== undefined ? disc.bim1.toFixed(1) : '—'}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'center', color: (disc.bim2 ?? 0) >= 6 ? '#16a34a' : (disc.bim2 !== null ? '#dc2626' : '#94a3b8'), fontWeight: disc.bim2 !== null ? 700 : 400 }}>
                              {disc.bim2 !== null && disc.bim2 !== undefined ? disc.bim2.toFixed(1) : '—'}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'center', color: (disc.bim3 ?? 0) >= 6 ? '#16a34a' : (disc.bim3 !== null ? '#dc2626' : '#94a3b8'), fontWeight: disc.bim3 !== null ? 700 : 400 }}>
                              {disc.bim3 !== null && disc.bim3 !== undefined ? disc.bim3.toFixed(1) : '—'}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'center', color: (disc.bim4 ?? 0) >= 6 ? '#16a34a' : (disc.bim4 !== null ? '#dc2626' : '#94a3b8'), fontWeight: disc.bim4 !== null ? 700 : 400 }}>
                              {disc.bim4 !== null && disc.bim4 !== undefined ? disc.bim4.toFixed(1) : '—'}
                            </td>
                            <td style={{ padding: '12px 12px', textAlign: 'center', fontWeight: 800, background: '#f8fafc', color: disc.media !== null && disc.media !== undefined ? (disc.media >= 6 ? '#16a34a' : '#dc2626') : '#94a3b8' }}>
                              {disc.media !== null && disc.media !== undefined ? disc.media.toFixed(1) : '—'}
                            </td>
                            <td style={{ padding: '12px 10px', textAlign: 'center', color: '#64748b', fontWeight: 600 }}>
                              {disc.faltas}
                            </td>
                            <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                              <span style={{
                                fontSize: '0.68rem', fontWeight: 800,
                                color: disc.situacao === 'APROVADO' ? '#16a34a' : disc.situacao === 'RECUPERACAO' ? '#d97706' : disc.situacao === 'REPROVADO' ? '#dc2626' : '#2563eb',
                                background: disc.situacao === 'APROVADO' ? '#dcfce7' : disc.situacao === 'RECUPERACAO' ? '#fef3c7' : disc.situacao === 'REPROVADO' ? '#fee2e2' : '#eff6ff',
                                padding: '3px 8px', borderRadius: 6,
                              }}>
                                {disc.situacao === 'APROVADO' ? 'Aprovado' : disc.situacao === 'RECUPERACAO' ? 'Recuperação' : disc.situacao === 'REPROVADO' ? 'Reprovado' : 'Em Curso'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f1f5f9', borderTop: '2px solid #e2e8f0', fontWeight: 800 }}>
                        <td style={{ padding: '12px 14px', color: '#0f172a' }}>MÉDIA GERAL DO ALUNO</td>
                        <td colSpan={4} style={{ textAlign: 'center', color: '#64748b', fontSize: '0.74rem' }}>
                          Ano Letivo em Andamento
                        </td>
                        <td style={{ padding: '12px 12px', textAlign: 'center', color: selectedFilho.mediaGlobal !== null && selectedFilho.mediaGlobal !== undefined ? (selectedFilho.mediaGlobal >= 6 ? '#16a34a' : '#dc2626') : '#94a3b8', fontSize: '0.95rem' }}>
                          {selectedFilho.mediaGlobal !== null && selectedFilho.mediaGlobal !== undefined ? selectedFilho.mediaGlobal.toFixed(1) : '—'}
                        </td>
                        <td style={{ padding: '12px 10px', textAlign: 'center', color: '#0f172a' }}>
                          {selectedFilho.faltasTotais}
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center', color: selectedFilho.frequenciaGlobal >= 75 ? '#16a34a' : '#dc2626' }}>
                          {selectedFilho.frequenciaGlobal >= 75 ? 'Apto / Regular' : 'Atenção (Faltas)'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Painel Lateral: Resumo Acadêmico & Regras */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Card Ficha do Aluno */}
              <div style={{
                background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 22,
                boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 14,
              }}>
                <h4 style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Award size={16} color="#2563eb" /> Resumo do Rendimento
                </h4>

                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <UserAvatar src={selectedFilho.fotoUrl} name={selectedFilho.nome} role="ALUNO" size="md" />
                  <div>
                    <div style={{ fontSize: '0.94rem', fontWeight: 800, color: '#0f172a' }}>{selectedFilho.nome}</div>
                    <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 2 }}>
                      {selectedFilho.turma?.nome || 'Turma não atribuída'} {selectedFilho.turma?.serie ? `• ${selectedFilho.turma.serie}` : ''}
                    </div>
                  </div>
                </div>

                <div style={{ height: 1, background: '#f1f5f9' }} />

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                    <span style={{ color: '#64748b' }}>Média Geral Global:</span>
                    <strong style={{ color: selectedFilho.mediaGlobal !== null && selectedFilho.mediaGlobal !== undefined && selectedFilho.mediaGlobal >= 6 ? '#16a34a' : '#0f172a' }}>
                      {selectedFilho.mediaGlobal !== null && selectedFilho.mediaGlobal !== undefined ? `${selectedFilho.mediaGlobal.toFixed(1)} / 10.0` : 'Aguardando notas'}
                    </strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                    <span style={{ color: '#64748b' }}>Frequência Acumulada:</span>
                    <strong style={{ color: selectedFilho.frequenciaGlobal >= 75 ? '#16a34a' : '#dc2626' }}>
                      {selectedFilho.frequenciaGlobal}%
                    </strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem' }}>
                    <span style={{ color: '#64748b' }}>Total de Faltas no Ano:</span>
                    <strong style={{ color: selectedFilho.faltasTotais > 0 ? '#dc2626' : '#16a34a' }}>
                      {selectedFilho.faltasTotais} {selectedFilho.faltasTotais === 1 ? 'aula' : 'aulas'}
                    </strong>
                  </div>
                </div>
              </div>

              {/* Card de Regras e Critérios do MEC / LDB */}
              <div style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)',
                borderRadius: 20, border: '1px solid #bfdbfe', padding: 20,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#1e40af', fontWeight: 800, fontSize: '0.86rem' }}>
                  <ShieldCheck size={16} color="#2563eb" /> Critérios de Aprovação Escolar
                </div>
                <div style={{ fontSize: '0.74rem', color: '#334155', lineHeight: 1.5, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div>
                    🎯 <strong>Média Mínima Anual:</strong> 6.0 por disciplina ao término dos 4 bimestres.
                  </div>
                  <div>
                    📊 <strong>Frequência Mínima (LDB):</strong> Exigência legal de 75% de assiduidade para aprovação sem dependência.
                  </div>
                  <div>
                    📝 <strong>Recuperação Paralela:</strong> Garantida a alunos com média bimestral inferior a 6.0.
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* ═══ ABA 2: GRÁFICOS E EVOLUÇÃO BIMESTRAL ═══ */
          <div style={{
            background: '#ffffff', borderRadius: 20, border: '1px solid #e2e8f0', padding: 24,
            boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', gap: 20,
          }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>
                Evolução do Rendimento por Bimestre
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: '0.76rem', color: '#64748b' }}>
                Acompanhamento temporal da média geral do aluno
              </p>
            </div>

            {selectedFilho.evolucao.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b', fontSize: '0.84rem' }}>
                Os gráficos comparativos serão gerados automaticamente após o fechamento do 1º bimestre.
              </div>
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={selectedFilho.evolucao} margin={{ top: 20, right: 30, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <YAxis domain={[0, 10]} tick={{ fill: '#64748b', fontSize: 12 }} />
                    <Tooltip />
                    <ReferenceLine y={6.0} stroke="#f43f5e" strokeDasharray="4 4" label={{ value: 'Média 6.0', fill: '#f43f5e', fontSize: 11 }} />
                    <Line type="monotone" dataKey="nota" name="Média do Aluno" stroke="#2563eb" strokeWidth={3} dot={{ r: 6, fill: '#2563eb' }} activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // ═══ MOBILE VIEW ═══
  return (
    <div className="resp-mobile-root" style={{ background: '#0b1526' }}>
      {/* ═══ NAVY HEADER ═══ */}
      <div className="resp-navy-header" style={{ padding: 'max(env(safe-area-inset-top, 40px), 40px) 0 24px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px', marginBottom: 16 }}>
          <button 
            onClick={() => navigate('/responsavel/dashboard')} 
            style={{ 
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: '50%', 
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', 
              color: 'white', cursor: 'pointer' 
            }}
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 800, color: 'white' }}>
              Boletim & Notas
            </h1>
            <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)' }}>
              Rendimento acadêmico oficial
            </p>
          </div>
        </div>

        {/* Seletor de Filho Mobile */}
        {filhos.length > 1 && (
          <div style={{ display: 'flex', gap: 8, padding: '0 20px', overflowX: 'auto', paddingBottom: 4 }}>
            {filhos.map((f) => (
              <button
                key={f.id}
                onClick={() => setSelectedFilhoId(f.id)}
                style={{
                  background: f.id === selectedFilhoId ? '#2563eb' : 'rgba(255,255,255,0.1)',
                  color: 'white', border: 'none', borderRadius: 20, padding: '6px 14px',
                  fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap', cursor: 'pointer',
                }}
              >
                {f.nome}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ═══ CONTENT AREA ═══ */}
      <div className="resp-content-area" style={{ paddingBottom: 90, marginTop: -16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Carregando notas...</div>
        ) : !selectedFilho ? (
          <div style={{ textAlign: 'center', padding: 32, background: 'white', borderRadius: 16, border: '1px solid #f1f5f9' }}>
            <BookOpen size={36} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '0.95rem', fontWeight: 700 }}>Nenhum dado encontrado</h3>
          </div>
        ) : selectedFilho.disciplinasBoletim.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, background: 'white', borderRadius: 16, border: '1px solid #f1f5f9' }}>
            <FileText size={36} style={{ color: '#cbd5e1', margin: '0 auto 12px' }} />
            <h3 style={{ margin: '0 0 6px', color: '#0f172a', fontSize: '0.95rem', fontWeight: 700 }}>Ciclo em Andamento</h3>
            <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0, lineHeight: 1.45 }}>
              As notas bimestrais serão publicadas no boletim assim que lançadas pelos professores.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {selectedFilho.disciplinasBoletim.map((disc) => (
              <div key={disc.materia} style={{ background: 'white', borderRadius: 14, padding: 14, border: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: '0.86rem', fontWeight: 800, color: '#0f172a' }}>{disc.materia}</span>
                  <span style={{
                    fontSize: '0.68rem', fontWeight: 800,
                    color: disc.situacao === 'APROVADO' ? '#16a34a' : disc.situacao === 'RECUPERACAO' ? '#d97706' : '#2563eb',
                    background: disc.situacao === 'APROVADO' ? '#dcfce7' : disc.situacao === 'RECUPERACAO' ? '#fef3c7' : '#eff6ff',
                    padding: '2px 6px', borderRadius: 6
                  }}>
                    {disc.situacao === 'APROVADO' ? 'Aprovado' : disc.situacao === 'RECUPERACAO' ? 'Recuperação' : 'Em Curso'}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, background: '#f8fafc', padding: 8, borderRadius: 8, textAlign: 'center', fontSize: '0.72rem' }}>
                  <div><span style={{ color: '#64748b' }}>1º B:</span> <strong>{disc.bim1 !== null && disc.bim1 !== undefined ? disc.bim1.toFixed(1) : '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>2º B:</span> <strong>{disc.bim2 !== null && disc.bim2 !== undefined ? disc.bim2.toFixed(1) : '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>3º B:</span> <strong>{disc.bim3 !== null && disc.bim3 !== undefined ? disc.bim3.toFixed(1) : '—'}</strong></div>
                  <div><span style={{ color: '#64748b' }}>4º B:</span> <strong>{disc.bim4 !== null && disc.bim4 !== undefined ? disc.bim4.toFixed(1) : '—'}</strong></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ═══ BARRA INFERIOR DE NAVEGAÇÃO MOBILE ═══ */}
      <ResponsavelTabBar activeTab="desempenho" />
    </div>
  );
}
